import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Plus, Search, Download, Trash2, FileText, Edit, Eye, 
  Upload, Loader2, Paperclip, File, X, ChevronDown, ChevronUp,
  Calendar, ArrowUpDown, ArrowUp, ArrowDown,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import MDEditor from '@uiw/react-md-editor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { expedientesAPI, adjuntosAPI } from '../api';
import { toast } from '../hooks/use-toast';

// Formatear tamaño de archivo
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const Actuaciones = () => {
  const [actuaciones, setActuaciones] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedActuaciones, setSelectedActuaciones] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedActuacion, setExpandedActuacion] = useState(null);
  const [actuacionesAdjuntos, setActuacionesAdjuntos] = useState({});
  
  // Paginación del servidor
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  
  // Debounce para búsqueda
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Estados para diálogos
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [editingActuacion, setEditingActuacion] = useState(null);
  const [viewingActuacion, setViewingActuacion] = useState(null);
  
  // Estados para formulario
  const [saving, setSaving] = useState(false);
  const [observaciones, setObservaciones] = useState('');
  const [editObservaciones, setEditObservaciones] = useState('');
  const [adjuntosTemp, setAdjuntosTemp] = useState([]);
  const [selectedExpediente, setSelectedExpediente] = useState('');
  const fileInputRef = useRef(null);
  
  // Estado para ordenación local (dentro de la página actual)
  const [sortConfig, setSortConfig] = useState({ key: 'fecha', direction: 'desc' });

  // Debounce del término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Cargar expedientes para el selector
  useEffect(() => {
    const loadExpedientes = async () => {
      try {
        const res = await expedientesAPI.getAll({ limit: 1000 });
        if (res.data && res.data.data) {
          setExpedientes(res.data.data);
        } else {
          setExpedientes(Array.isArray(res.data) ? res.data : []);
        }
      } catch (error) {
        console.error('Error cargando expedientes:', error);
      }
    };
    loadExpedientes();
  }, []);

  // Cargar actuaciones con paginación del servidor
  const loadActuaciones = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      if (debouncedSearch && debouncedSearch.length >= 2) {
        params.search = debouncedSearch;
      }
      
      const res = await expedientesAPI.getAllActuaciones(params);
      
      if (res.data && res.data.data) {
        setActuaciones(res.data.data);
        setPagination(prev => ({
          ...prev,
          ...res.data.pagination
        }));
        
        // Cargar adjuntos de las actuaciones visibles
        const adjuntosMap = {};
        for (const act of res.data.data) {
          try {
            const adjRes = await adjuntosAPI.getByActuacion(act.id);
            adjuntosMap[act.id] = adjRes.data;
          } catch (err) {
            adjuntosMap[act.id] = [];
          }
        }
        setActuacionesAdjuntos(adjuntosMap);
      } else {
        // Fallback para compatibilidad
        setActuaciones(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error('Error cargando actuaciones:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las actuaciones',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, debouncedSearch]);

  useEffect(() => {
    loadActuaciones();
  }, [loadActuaciones]);

  // Cambiar página
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
      setExpandedActuacion(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Ordenar actuaciones localmente (dentro de la página actual)
  const sortedActuaciones = [...actuaciones].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortConfig.key) {
      case 'fecha':
        aValue = new Date(a.fecha || 0);
        bValue = new Date(b.fecha || 0);
        break;
      case 'titulo':
        aValue = (a.titulo || '').toLowerCase();
        bValue = (b.titulo || '').toLowerCase();
        break;
      case 'expediente':
        aValue = (a.expedienteNumero || '').toLowerCase();
        bValue = (b.expedienteNumero || '').toLowerCase();
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Función para cambiar ordenación
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Componente para mostrar icono de ordenación
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-3 h-3 ml-1 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedActuaciones(sortedActuaciones.map(act => act.id));
    } else {
      setSelectedActuaciones([]);
    }
  };

  const handleSelectActuacion = (id, checked) => {
    if (checked) {
      setSelectedActuaciones([...selectedActuaciones, id]);
    } else {
      setSelectedActuaciones(selectedActuaciones.filter(actId => actId !== id));
    }
  };

  const handleBorrarSeleccion = async () => {
    if (selectedActuaciones.length === 0) {
      toast({
        title: 'Selección vacía',
        description: 'No hay actuaciones seleccionadas para borrar',
        variant: 'destructive'
      });
      return;
    }

    if (!window.confirm(`¿Eliminar ${selectedActuaciones.length} actuación(es)?`)) return;

    let deletedCount = 0;
    for (const actId of selectedActuaciones) {
      try {
        await expedientesAPI.deleteActuacion(actId);
        deletedCount++;
      } catch (err) {
        console.error('Error eliminando actuación:', err);
      }
    }

    setSelectedActuaciones([]);
    toast({
      title: 'Actuaciones eliminadas',
      description: `${deletedCount} actuación(es) eliminada(s) correctamente`,
    });
    loadActuaciones();
  };

  const handleExportar = () => {
    toast({
      title: 'Exportando...',
      description: 'Preparando archivo para descarga',
    });
  };

  // Crear nueva actuación
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!selectedExpediente) {
      toast({
        title: 'Error',
        description: 'Debes seleccionar un expediente',
        variant: 'destructive'
      });
      return;
    }
    
    setSaving(true);
    const formData = new FormData(e.target);
    
    const data = {
      titulo: formData.get('titulo'),
      tipo: formData.get('tipo') || 'general',
      descripcion: observaciones,
      fecha: formData.get('fecha'),
      duracion: formData.get('duracion')
    };
    
    try {
      const response = await expedientesAPI.createActuacion(selectedExpediente, data);
      const newActuacion = response.data;
      
      // Subir adjuntos si hay
      if (adjuntosTemp.length > 0 && newActuacion?.id) {
        for (const file of adjuntosTemp) {
          const uploadData = new FormData();
          uploadData.append('file', file);
          uploadData.append('tipo_adjunto', 'documento');
          try {
            await adjuntosAPI.uploadActuacion(newActuacion.id, uploadData);
          } catch (err) {
            console.error('Error subiendo adjunto:', err);
          }
        }
      }
      
      toast({ title: 'Actuación creada correctamente' });
      setIsNewDialogOpen(false);
      setObservaciones('');
      setAdjuntosTemp([]);
      setSelectedExpediente('');
      loadActuaciones();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo crear la actuación',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Editar actuación
  const handleEditClick = (act) => {
    setEditingActuacion(act);
    setEditObservaciones(act.observaciones || act.descripcion || '');
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const formData = new FormData(e.target);
    const data = {
      titulo: formData.get('titulo'),
      tipo: editingActuacion.tipo || 'general',
      descripcion: editObservaciones,
      fecha: formData.get('fecha')
    };
    
    try {
      await expedientesAPI.updateActuacion(editingActuacion.id, data);
      toast({ title: 'Actuación actualizada' });
      setIsEditDialogOpen(false);
      setEditingActuacion(null);
      setEditObservaciones('');
      loadActuaciones();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la actuación',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Ver actuación
  const handleViewClick = (act) => {
    setViewingActuacion(act);
    setIsViewDialogOpen(true);
  };

  // Eliminar actuación individual
  const handleDeleteClick = async (act) => {
    if (!window.confirm(`¿Eliminar la actuación "${act.titulo}"?`)) return;
    
    try {
      await expedientesAPI.deleteActuacion(act.id);
      toast({ title: 'Actuación eliminada' });
      loadActuaciones();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la actuación',
        variant: 'destructive'
      });
    }
  };

  // Manejar archivos temporales
  const handleAddTempFiles = (files) => {
    setAdjuntosTemp(prev => [...prev, ...Array.from(files)]);
  };

  const handleRemoveTempFile = (index) => {
    setAdjuntosTemp(prev => prev.filter((_, i) => i !== index));
  };

  // Subir adjunto a actuación existente
  const handleUploadAdjunto = async (actuacionId, files) => {
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo_adjunto', 'documento');
      
      try {
        await adjuntosAPI.uploadActuacion(actuacionId, formData);
        toast({ title: `Archivo "${file.name}" subido` });
      } catch (error) {
        toast({
          title: 'Error',
          description: `No se pudo subir ${file.name}`,
          variant: 'destructive'
        });
      }
    }
    loadActuaciones();
  };

  // Descargar adjunto
  const handleDownloadAdjunto = async (adjunto) => {
    try {
      const response = await adjuntosAPI.download(adjunto.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', adjunto.nombre);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo descargar', variant: 'destructive' });
    }
  };

  // Eliminar adjunto
  const handleDeleteAdjunto = async (adjuntoId) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    try {
      await adjuntosAPI.delete(adjuntoId);
      toast({ title: 'Archivo eliminado' });
      loadActuaciones();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  // Toggle expandir actuación
  const toggleExpand = (actId) => {
    setExpandedActuacion(expandedActuacion === actId ? null : actId);
  };

  // Componente de paginación
  const PaginationControls = () => {
    const { page, total_pages, total, has_next, has_prev, limit } = pagination;
    const startRecord = total > 0 ? (page - 1) * limit + 1 : 0;
    const endRecord = Math.min(page * limit, total);

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 bg-gray-50 border-t">
        <div className="text-sm text-gray-600">
          Mostrando <span className="font-medium">{startRecord}</span> a <span className="font-medium">{endRecord}</span> de <span className="font-medium">{total}</span> actuaciones
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handlePageChange(1)}
            disabled={!has_prev}
            title="Primera página"
          >
            <ChevronsLeft className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handlePageChange(page - 1)}
            disabled={!has_prev}
            title="Página anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-1 px-2">
            <span className="text-sm font-medium">Página</span>
            <Input 
              type="number" 
              min={1} 
              max={total_pages}
              value={page}
              onChange={(e) => {
                const newPage = parseInt(e.target.value);
                if (!isNaN(newPage) && newPage >= 1 && newPage <= total_pages) {
                  handlePageChange(newPage);
                }
              }}
              className="w-16 h-8 text-center text-sm"
            />
            <span className="text-sm text-gray-500">de {total_pages}</span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handlePageChange(page + 1)}
            disabled={!has_next}
            title="Página siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handlePageChange(total_pages)}
            disabled={!has_next}
            title="Última página"
          >
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  if (loading && actuaciones.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Actuaciones</h1>
          <p className="text-sm text-gray-600 mt-1">
            Estos son todos los elementos que se han creado en Lexduo.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Pulsa en los títulos de las columnas para ordenar y utiliza el buscador para filtrar.
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="bg-orange-500 text-white hover:bg-orange-600 border-orange-500"
            onClick={() => toast({ title: 'Informes', description: 'Funcionalidad en desarrollo' })}
          >
            <FileText className="w-4 h-4 mr-2" />
            INFORMES
          </Button>
          <Button 
            variant="outline" 
            className="bg-blue-600 text-white hover:bg-blue-700 border-blue-600"
            onClick={handleExportar}
          >
            <Download className="w-4 h-4 mr-2" />
            EXPORTAR
          </Button>
          <Button 
            variant="outline" 
            className="bg-blue-700 text-white hover:bg-blue-800 border-blue-700"
            onClick={handleBorrarSeleccion}
            disabled={selectedActuaciones.length === 0}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            BORRAR SELECCIÓN
          </Button>
        </div>
      </div>

      {/* Buscador */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Buscar en título, descripción, tipo, expediente..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-actuaciones-input"
              />
            </div>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => setIsNewDialogOpen(true)}
              data-testid="new-actuacion-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              NUEVA
            </Button>
          </div>
          {debouncedSearch && (
            <div className="mt-2 text-sm text-gray-500">
              Buscando: "<span className="font-medium">{debouncedSearch}</span>" - {pagination.total} resultados encontrados
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabla de Actuaciones */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="w-12 px-4 py-3">
                        <Checkbox 
                          checked={selectedActuaciones.length === sortedActuaciones.length && sortedActuaciones.length > 0}
                          onCheckedChange={handleSelectAll}
                        />
                      </th>
                      <th className="w-8 px-2 py-3"></th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('expediente')}
                      >
                        <div className="flex items-center">
                          Expediente
                          <SortIcon columnKey="expediente" />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('titulo')}
                      >
                        <div className="flex items-center">
                          Título
                          <SortIcon columnKey="titulo" />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('fecha')}
                      >
                        <div className="flex items-center">
                          Fecha
                          <SortIcon columnKey="fecha" />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                        Adjuntos
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedActuaciones.length > 0 ? (
                      sortedActuaciones.map((act) => {
                        const adjuntos = actuacionesAdjuntos[act.id] || [];
                        const isExpanded = expandedActuacion === act.id;
                        
                        return (
                          <React.Fragment key={act.id}>
                            <tr className="border-b hover:bg-gray-50 transition-colors" data-testid={`actuacion-row-${act.id}`}>
                              <td className="px-4 py-3">
                                <Checkbox 
                                  checked={selectedActuaciones.includes(act.id)}
                                  onCheckedChange={(checked) => handleSelectActuacion(act.id, checked)}
                                />
                              </td>
                              <td className="px-2 py-3">
                                <button 
                                  onClick={() => toggleExpand(act.id)}
                                  className="p-1 hover:bg-gray-200 rounded"
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="w-4 h-4 text-gray-500" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                  )}
                                </button>
                              </td>
                              <td className="px-4 py-3">
                                <button className="text-sm font-medium text-blue-600 hover:underline">
                                  {act.expedienteNumero || act.expediente_codigo}
                                </button>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-gray-900 line-clamp-2">
                                  {act.titulo}
                                </p>
                              </td>
                              <td className="px-4 py-3 text-sm whitespace-nowrap">
                                {act.fecha ? new Date(act.fecha).toLocaleDateString('es-ES') : '-'}
                              </td>
                              <td className="px-4 py-3">
                                {adjuntos.length > 0 ? (
                                  <Badge variant="secondary" className="text-xs">
                                    <Paperclip className="w-3 h-3 mr-1" />
                                    {adjuntos.length}
                                  </Badge>
                                ) : (
                                  <span className="text-gray-400 text-xs">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-1">
                                  <button 
                                    className="p-1 hover:bg-gray-200 rounded"
                                    title="Ver actuación"
                                    onClick={() => handleViewClick(act)}
                                  >
                                    <Eye className="w-4 h-4 text-gray-600" />
                                  </button>
                                  <button 
                                    className="p-1 hover:bg-gray-200 rounded"
                                    title="Editar"
                                    onClick={() => handleEditClick(act)}
                                  >
                                    <Edit className="w-4 h-4 text-gray-600" />
                                  </button>
                                  <label className="p-1 hover:bg-gray-200 rounded cursor-pointer" title="Adjuntar archivo">
                                    <Upload className="w-4 h-4 text-blue-600" />
                                    <input
                                      type="file"
                                      className="hidden"
                                      multiple
                                      onChange={(e) => handleUploadAdjunto(act.id, Array.from(e.target.files || []))}
                                    />
                                  </label>
                                  <button 
                                    className="p-1 hover:bg-gray-200 rounded"
                                    title="Eliminar"
                                    onClick={() => handleDeleteClick(act)}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            
                            {/* Fila expandida con adjuntos */}
                            {isExpanded && (
                              <tr className="bg-gray-50">
                                <td colSpan="7" className="px-4 py-4">
                                  <div className="space-y-3">
                                    {/* Descripción */}
                                    {(act.descripcion || act.observaciones) && (
                                      <div className="bg-white p-3 rounded border">
                                        <p className="text-sm font-medium text-gray-700 mb-2">Observaciones:</p>
                                        <div className="prose prose-sm max-w-none" data-color-mode="light">
                                          <MDEditor.Markdown source={act.descripcion || act.observaciones} />
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Adjuntos */}
                                    <div className="bg-white p-3 rounded border">
                                      <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm font-medium text-gray-700 flex items-center gap-1">
                                          <Paperclip className="w-4 h-4" />
                                          Archivos adjuntos ({adjuntos.length})
                                        </p>
                                        <label>
                                          <Button size="sm" variant="outline" className="cursor-pointer" asChild>
                                            <span>
                                              <Upload className="w-4 h-4 mr-1" />
                                              Adjuntar
                                            </span>
                                          </Button>
                                          <input
                                            type="file"
                                            className="hidden"
                                            multiple
                                            onChange={(e) => handleUploadAdjunto(act.id, Array.from(e.target.files || []))}
                                          />
                                        </label>
                                      </div>
                                      
                                      {adjuntos.length > 0 ? (
                                        <div className="space-y-1">
                                          {adjuntos.map((adj) => (
                                            <div key={adj.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded text-sm">
                                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                <button
                                                  onClick={() => window.open(`/preview/${adj.id}`, '_blank')}
                                                  className="text-blue-600 hover:underline truncate"
                                                  title={adj.nombre}
                                                >
                                                  {adj.nombre}
                                                </button>
                                                <span className="text-gray-400 text-xs flex-shrink-0">
                                                  ({formatFileSize(adj.tamano)})
                                                </span>
                                              </div>
                                              <div className="flex items-center gap-1 flex-shrink-0">
                                                <Button 
                                                  variant="ghost" 
                                                  size="sm" 
                                                  className="h-7 w-7 p-0"
                                                  onClick={() => window.open(`/preview/${adj.id}`, '_blank')}
                                                  title="Previsualizar"
                                                >
                                                  <Eye className="w-4 h-4 text-green-600" />
                                                </Button>
                                                <Button 
                                                  variant="ghost" 
                                                  size="sm" 
                                                  className="h-7 w-7 p-0"
                                                  onClick={() => handleDownloadAdjunto(adj)}
                                                  title="Descargar"
                                                >
                                                  <Download className="w-4 h-4 text-blue-600" />
                                                </Button>
                                                <Button 
                                                  variant="ghost" 
                                                  size="sm" 
                                                  className="h-7 w-7 p-0"
                                                  onClick={() => handleDeleteAdjunto(adj.id)}
                                                  title="Eliminar"
                                                >
                                                  <Trash2 className="w-4 h-4 text-red-600" />
                                                </Button>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="text-sm text-gray-500 text-center py-4">
                                          No hay archivos adjuntos
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p className="text-sm">{debouncedSearch ? 'No se encontraron actuaciones con esa búsqueda' : 'No se encontraron actuaciones'}</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Paginación */}
              {pagination.total > 0 && <PaginationControls />}
            </>
          )}
        </CardContent>
      </Card>

      {/* Info footer */}
      {selectedActuaciones.length > 0 && (
        <div className="text-xs text-gray-500 text-center">
          {selectedActuaciones.length} actuación(es) seleccionada(s)
        </div>
      )}

      {/* Dialog Nueva Actuación */}
      <Dialog open={isNewDialogOpen} onOpenChange={setIsNewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva actuación</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSubmit}>
            <div className="space-y-4">
              <div>
                <Label>Expediente *</Label>
                <Select value={selectedExpediente} onValueChange={setSelectedExpediente}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar expediente" />
                  </SelectTrigger>
                  <SelectContent>
                    {expedientes.map(exp => (
                      <SelectItem key={exp.id} value={String(exp.id)}>
                        {exp.numero_expediente} - {exp.titulo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Título *</Label>
                <Input name="titulo" required />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select name="tipo" defaultValue="general">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="llamada">Llamada telefónica</SelectItem>
                      <SelectItem value="reunion">Reunión</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="nota">Nota interna</SelectItem>
                      <SelectItem value="escrito">Escrito</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fecha</Label>
                  <Input name="fecha" type="date" defaultValue={new Date().toISOString().split('T')[0]} />
                </div>
              </div>
              
              <div>
                <Label>Duración</Label>
                <Input name="duracion" type="time" step="1" placeholder="00:00:00" />
              </div>
              
              <div data-color-mode="light">
                <Label>Observaciones</Label>
                <div className="mt-1">
                  <MDEditor
                    value={observaciones}
                    onChange={setObservaciones}
                    preview="edit"
                    height={200}
                    textareaProps={{
                      placeholder: 'Escribe las observaciones... (soporta formato Markdown)'
                    }}
                  />
                </div>
              </div>
              
              {/* Sección de adjuntos */}
              <div>
                <Label className="flex items-center gap-2">
                  <Paperclip className="w-4 h-4" /> Archivos adjuntos
                </Label>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  multiple
                  onChange={(e) => handleAddTempFiles(e.target.files)}
                />
                <div 
                  className="mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-gray-50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Haz clic para seleccionar archivos</p>
                  <p className="text-xs text-gray-400 mt-1">o arrastra y suelta aquí</p>
                </div>
                
                {adjuntosTemp.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-sm font-medium text-gray-700">Archivos a adjuntar:</p>
                    {adjuntosTemp.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between py-1 px-2 bg-gray-100 rounded text-sm">
                        <div className="flex items-center gap-2">
                          <File className="w-4 h-4 text-gray-400" />
                          <span className="truncate">{file.name}</span>
                          <span className="text-gray-400 text-xs">({formatFileSize(file.size)})</span>
                        </div>
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="sm" 
                          className="h-7 w-7 p-0"
                          onClick={() => handleRemoveTempFile(idx)}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => {
                setIsNewDialogOpen(false);
                setObservaciones('');
                setAdjuntosTemp([]);
                setSelectedExpediente('');
              }}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar Actuación */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar actuación</DialogTitle>
          </DialogHeader>
          {editingActuacion && (
            <form onSubmit={handleEditSubmit}>
              <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>Expediente:</strong> {editingActuacion.expedienteNumero || editingActuacion.expediente_codigo}
                  </p>
                </div>
                
                <div>
                  <Label>Título *</Label>
                  <Input name="titulo" defaultValue={editingActuacion.titulo} required />
                </div>
                
                <div>
                  <Label>Fecha</Label>
                  <Input 
                    name="fecha" 
                    type="date" 
                    defaultValue={editingActuacion.fecha?.split('T')[0] || ''} 
                  />
                </div>
                
                <div data-color-mode="light">
                  <Label>Observaciones</Label>
                  <div className="mt-1">
                    <MDEditor
                      value={editObservaciones}
                      onChange={setEditObservaciones}
                      preview="edit"
                      height={200}
                    />
                  </div>
                </div>
                
                {/* Adjuntos existentes */}
                {actuacionesAdjuntos[editingActuacion.id]?.length > 0 && (
                  <div>
                    <Label className="flex items-center gap-2">
                      <Paperclip className="w-4 h-4" /> Archivos adjuntos existentes
                    </Label>
                    <div className="mt-2 space-y-1">
                      {actuacionesAdjuntos[editingActuacion.id].map((adj) => (
                        <div key={adj.id} className="flex items-center justify-between py-1 px-2 bg-gray-100 rounded text-sm">
                          <div className="flex items-center gap-2">
                            <File className="w-4 h-4 text-gray-400" />
                            <span className="truncate">{adj.nombre}</span>
                            <span className="text-gray-400 text-xs">({formatFileSize(adj.tamano)})</span>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm" 
                              className="h-7 w-7 p-0"
                              onClick={() => handleDownloadAdjunto(adj)}
                            >
                              <Download className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm" 
                              className="h-7 w-7 p-0"
                              onClick={() => handleDeleteAdjunto(adj.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Añadir nuevos adjuntos */}
                <div>
                  <Label>Añadir más archivos</Label>
                  <label className="mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-gray-50 transition-colors block">
                    <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                    <p className="text-sm text-gray-600">Haz clic para seleccionar archivos</p>
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      onChange={(e) => handleUploadAdjunto(editingActuacion.id, Array.from(e.target.files || []))}
                    />
                  </label>
                </div>
              </div>
              
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingActuacion(null);
                  setEditObservaciones('');
                }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Guardar cambios
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Ver Actuación */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle de actuación</DialogTitle>
          </DialogHeader>
          {viewingActuacion && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Expediente</p>
                  <p className="font-medium">{viewingActuacion.expedienteNumero || viewingActuacion.expediente_codigo}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Fecha</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {viewingActuacion.fecha ? new Date(viewingActuacion.fecha).toLocaleDateString('es-ES') : '-'}
                  </p>
                </div>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Título</p>
                <p className="font-medium">{viewingActuacion.titulo}</p>
              </div>
              
              {(viewingActuacion.descripcion || viewingActuacion.observaciones) && (
                <div className="p-3 bg-white border rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Observaciones</p>
                  <div className="prose prose-sm max-w-none" data-color-mode="light">
                    <MDEditor.Markdown source={viewingActuacion.descripcion || viewingActuacion.observaciones} />
                  </div>
                </div>
              )}
              
              {/* Adjuntos */}
              <div className="p-3 bg-white border rounded-lg">
                <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                  <Paperclip className="w-4 h-4" /> Archivos adjuntos
                </p>
                {actuacionesAdjuntos[viewingActuacion.id]?.length > 0 ? (
                  <div className="space-y-1">
                    {actuacionesAdjuntos[viewingActuacion.id].map((adj) => (
                      <div key={adj.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded text-sm">
                        <div className="flex items-center gap-2">
                          <File className="w-4 h-4 text-gray-400" />
                          <span>{adj.nombre}</span>
                          <span className="text-gray-400 text-xs">({formatFileSize(adj.tamano)})</span>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0"
                            onClick={() => window.open(`/preview/${adj.id}`, '_blank')}
                          >
                            <Eye className="w-4 h-4 text-green-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0"
                            onClick={() => handleDownloadAdjunto(adj)}
                          >
                            <Download className="w-4 h-4 text-blue-600" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No hay archivos adjuntos
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              Cerrar
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                setIsViewDialogOpen(false);
                handleEditClick(viewingActuacion);
              }}
            >
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Actuaciones;
